import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle, CheckCircle2, ChevronDown, FileCheck, FileText, Info, Loader2, Plus,
  Save, Search, Trash2, UploadCloud, X,
} from "lucide-react";
import {
  useGetExamApplicationFormQuery,
  useSaveExamApplicationFormMutation,
  useLookupExamSponsorMutation,
} from "../api/examCallsApi";
// No validateSittings: this form saves what it has and reports what is
// outstanding — the O'level rules are enforced at submission, by the server.
import OlevelSittings, {
  blankSitting,
  storageUrl,
  toEditableSitting,
  type Sitting,
} from "./OlevelSittings";

/**
 * The NIQS exam application form (TPC / PCI), section by section, as it appears
 * on the paper form. Shown at finalize for exams flagged requires_application_form.
 *
 * An unsaved form arrives pre-filled from the member record and the candidate's
 * probationer application, so most people are confirming rather than typing.
 */

const input =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900";
const label = "block text-xs font-medium text-gray-600 mb-1";

/**
 * Flat subject rows regrouped into the SITTINGS they were taken in.
 *
 * A candidate sat an exam, not a subject: one board, one year, one exam number
 * and one scanned slip, with the subjects taken in that sitting inside it. The
 * table underneath is one row per subject — the server expands on save and this
 * regroups on read — so the candidate is asked the way they were examined,
 * which is how the probationer application asks too.
 */
function groupIntoSittings(rows: any[]): Sitting[] {
  const map = new Map<string, any>();

  for (const r of rows) {
    const key = [r.exam_type ?? "", r.exam_year ?? "", r.exam_number ?? ""].join("|");

    if (!map.has(key)) {
      map.set(key, {
        exam_type: r.exam_type ?? "waec",
        exam_year: r.exam_year ?? "",
        exam_number: r.exam_number ?? "",
        result_file: r.result_file ?? null,
        subjects: [] as any[],
      });
    }

    const sitting = map.get(key);
    sitting.subjects.push({ subject: r.subject ?? "", grade: r.grade ?? "" });
    // Any row of the sitting may be the one carrying the slip.
    sitting.result_file = sitting.result_file ?? r.result_file ?? null;
  }

  return Array.from(map.values()).map(toEditableSitting);
}

function Section({
  n, title, hint, children, defaultOpen = true,
}: {
  n: string; title: string; hint?: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50/70"
      >
        <span className="text-[11px] font-black text-gray-400 tabular-nums">{n}</span>
        <span className="flex-1">
          <span className="block font-semibold text-gray-900">{title}</span>
          {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 border-t pt-4">{children}</div>}
    </div>
  );
}

/**
 * A personal detail as held by the Institute — shown, not asked for.
 *
 * Section 1 is read back from the member record rather than typed: the form is
 * a declaration of what NIQS holds, and a candidate correcting their name or
 * date of birth here would leave their actual member record untouched. Profile
 * changes belong in the profile.
 */
function Held({ label: text, value, wide = false }: { label: string; value: any; wide?: boolean }) {
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <label className={label}>{text}</label>
      <p className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-800 whitespace-pre-line min-h-[2.375rem]">
        {value === 0 || value ? value : "—"}
      </p>
    </div>
  );
}

const GENDERS: Record<string, string> = { M: "Male", F: "Female", male: "Male", female: "Female" };

/** A released-on date, short enough to sit inline in a list. */
function fmtDay(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  } catch {
    return String(iso);
  }
}

/** A repeatable set of rows with add/remove, used by sections 3, 4, 5 and 7. */
function Rows({
  items, onAdd, onRemove, addLabel, empty, children,
}: {
  items: any[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  addLabel: string;
  empty: string;
  children: (row: any, i: number) => React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-sm text-gray-500">{empty}</p>}

      {items.map((row, i) => (
        <div key={i} className="relative border border-gray-100 rounded-xl p-3 pr-10 bg-gray-50/40">
          {children(row, i)}
          <button
            type="button"
            onClick={() => onRemove(i)}
            title="Remove"
            className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-300 hover:text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold text-gray-600 hover:bg-gray-50"
      >
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );
}

export default function ExamApplicationFormSections({
  applicationId,
  readOnly = false,
  onSaved,
}: {
  applicationId: string;
  readOnly?: boolean;
  onSaved?: (complete: boolean) => void;
}) {
  const { data, isLoading, isError } = useGetExamApplicationFormQuery(applicationId, {
    skip: !applicationId,
  });
  // A 200 carrying only a `message` is the API failing without a status (see
  // loadError in ExamFinalizePage) — an error body must not read as an empty form.
  const loadFailed = isError || (!!data && !data.form && !!data.message);
  const [save, { isLoading: saving }] = useSaveExamApplicationFormMutation();
  const [lookupSponsor, { isLoading: looking }] = useLookupExamSponsorMutation();

  const [form, setForm] = useState<any>({});
  const [sittings, setSittings] = useState<Sitting[]>([]);
  const [educations, setEducations] = useState<any[]>([]);
  const [memberships, setMemberships] = useState<any[]>([]);
  const [experiences, setExperiences] = useState<any[]>([]);
  const [otherExams, setOtherExams] = useState<any[]>([]);
  const [employer, setEmployer] = useState<any>({});
  const [sponsor, setSponsor] = useState<any>({});
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  // Outcome of the last sponsor lookup, so the candidate knows whether the
  // details below came from us or are still theirs to type.
  const [sponsorHit, setSponsorHit] = useState<{ ok: boolean; text: string } | null>(null);

  const requiredOlevels = data?.required_olevel_subjects ?? 5;
  const prefilled = !!data?.prefilled;
  const centres: any[] = data?.centres ?? [];
  const chapters: any[] = data?.chapters ?? [];
  // A member with no chapter on file is asked for one here; anyone who already
  // has one just sees it.
  const needsChapter = !data?.member_chapter;
  const systemLogbooks: any[] = data?.system_record?.logbooks ?? [];
  const systemExams: any[] = data?.system_record?.exams ?? [];

  // Dates arrive as ISO strings; <input type="date"> wants YYYY-MM-DD.
  const day = (v: any) => (v ? String(v).slice(0, 10) : "");

  useEffect(() => {
    if (!data) return;
    setForm({
      ...(data.form ?? {}),
      date_of_birth: day(data.form?.date_of_birth),
      date_of_registration: day(data.form?.date_of_registration),
    });
    // One sitting to start on an application with nothing entered yet, so the
    // candidate is looking at the questions rather than at an empty panel.
    const grouped = groupIntoSittings(data.olevels ?? []);
    setSittings(grouped.length ? grouped : [blankSitting()]);
    setEducations((data.educations ?? []).map((e: any) => ({ ...e, completed_on: day(e.completed_on) })));
    setMemberships((data.memberships ?? []).map((m: any) => ({ ...m })));
    setExperiences(
      (data.experiences ?? []).map((e: any) => ({
        ...e,
        start_date: day(e.start_date),
        end_date: day(e.end_date),
      }))
    );
    setOtherExams((data.other_exams ?? []).map((x: any) => ({ ...x })));
    setEmployer({ ...(data.employer ?? {}), employment_date: day(data.employer?.employment_date) });
    // The saved sponsor arrives without a registration date and is not asked
    // for one — nothing on this screen types or shows it.
    setSponsor({ ...(data.sponsor ?? {}) });
  }, [data]);

  const setField = (setter: any) => (key: string, value: any) =>
    setter((prev: any) => ({ ...prev, [key]: value }));

  const setRow = (setter: any) => (i: number, key: string, value: any) =>
    setter((prev: any[]) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const setF = setField(setForm);
  const setE = setField(setEmployer);
  // No setter for the sponsor: nothing about them is typed. The search writes
  // that section whole — see onFindSponsor.

  // Subjects across every sitting — what `requiredOlevels` counts.
  const olevelCount = sittings.reduce(
    (n, s) => n + s.subjects.filter((x) => x.subject.trim() && x.grade.trim()).length,
    0,
  );
  const olevelShort = olevelCount < requiredOlevels;

  // Required by missingSections(), but read-only here — so name any that are
  // blank rather than letting the candidate hunt for why they cannot submit.
  const heldGaps = useMemo(
    () =>
      [
        ["surname", "your surname"],
        ["other_names", "your other names"],
        ["phone", "a phone number"],
        ["email", "an email address"],
      ]
        .filter(([key]) => !String(form?.[key] ?? "").trim())
        .map(([, text]) => text),
    [form]
  );

  /**
   * Pull the sponsor's particulars off their member record.
   *
   * This is the ONLY way the sponsor's details get onto the form — they are not
   * typed, and nothing below the search is editable. A sponsor is a particular
   * member, and the form has to say who the Institute holds rather than what
   * the candidate wrote: a "corrected" spelling or a remembered grade turns a
   * sponsorship into a claim about somebody who may not exist. The member
   * record answers, or there is no sponsor.
   *
   * Replaces rather than merges, so nothing from a previous search survives
   * into a new one.
   */
  const onFindSponsor = async () => {
    const membershipNo = String(sponsor.membership_no ?? "").trim();
    if (!membershipNo) return;

    setSponsorHit(null);
    try {
      const res: any = await lookupSponsor({ id: applicationId, membership_no: membershipNo }).unwrap();
      const m = res?.member ?? {};

      // No registration date: the server does not send one, and the stored
      // sponsor record takes it from the member record at save time.
      setSponsor({
        membership_no: m.membership_no ?? membershipNo,
        full_name: m.full_name ?? null,
        email: m.email ?? null,
        membership_grade: m.membership_grade ?? null,
        contact_address: m.contact_address ?? null,
      });

      setSponsorHit({
        ok: true,
        text: `Matched ${m.full_name ?? membershipNo}${m.membership_grade ? ` — ${m.membership_grade}` : ""}.`,
      });
    } catch (e: any) {
      // The number named nobody, or named someone who may not sponsor (dues
      // owing, or barred by the Institute). Either way the form must not keep
      // whoever it named before — the candidate would save a sponsor they had
      // just replaced. The server's message says which it was.
      setSponsor({ membership_no: membershipNo });
      setSponsorHit({
        ok: false,
        text: e?.data?.message || "No member found with that membership number.",
      });
    }
  };

  const onSave = async () => {
    setBanner(null);

    /* NOTHING IS BLOCKED HERE. This form is filled over several sittings at a
     * keyboard, not in one go: a candidate must be able to save what they have
     * and come back to the rest, which is what the `missing` list the server
     * answers with is for. Refusing the whole document because one section is
     * half-finished loses the other eight.
     *
     * So the O'level rules (five graded subjects, the compulsory three, a
     * scanned slip) are NOT applied at save. They are applied at SUBMIT, by the
     * server, where they belong. */

    try {
      // Blank rows are the user's way of abandoning a line they added.
      const qualifications = educations.filter((e) => e.institution && e.qualification);

      /* A sitting is savable once it has a year and at least one graded
       * subject. Anything less is a panel the candidate has opened and not yet
       * filled in — sending it would fail validation on a section they have not
       * claimed to be done with. */
      const savable = sittings
        .map(({ file, ...rest }: any) => ({
          ...rest,
          subjects: rest.subjects.filter((x: any) => x.subject.trim() && x.grade.trim()),
        }))
        .filter((s: any) => String(s.exam_year ?? "").trim() && s.subjects.length > 0);

      const document = {
        form,
        // O'level travels as SITTINGS — the shape the candidate was asked in,
        // which the server expands into the subject rows the table holds.
        sittings: savable,
        // The SAME O'level, flattened, for a server that predates `sittings`.
        // Without it that server reads the request as "no O'level at all" and
        // clears the section; with it, the older shape still saves and the
        // newer one wins wherever both are understood. `file` cannot ride in
        // JSON either way — new scans go alongside, indexed to match.
        olevels: savable.flatMap((s: any) =>
          s.subjects.map((x: any) => ({
            subject: x.subject.trim(),
            grade: x.grade.trim().toUpperCase(),
            exam_type: s.exam_type || "waec",
            exam_year: s.exam_year,
            exam_number: s.exam_number || null,
            result_file: s.result_file ?? null,
          })),
        ),
        // `file` is a File and cannot ride in JSON — it goes alongside, indexed
        // to match. Everything else about the row travels as normal.
        educations: qualifications.map(({ file, ...rest }: any) => rest),
        memberships: memberships.filter((m) => m.institution),
        experiences: experiences.filter((e) => e.employer_name && e.position),
        other_exams: otherExams.filter((x) => x.exam_name),
        employer,
        sponsor,
      };

      // Only go multipart when there is actually a file to carry. A plain JSON
      // save is the far commoner case and stays exactly as it was.
      const picked = qualifications
        .map((q: any, i: number) => ({ file: q.file, i }))
        .filter((x) => x.file);

      /* Indexed against the SAVABLE list, not the state: `sitting_files[2]` is
         the scan for the third sitting the server is about to be sent, and a
         half-filled panel dropped above would otherwise shift every scan after
         it onto the wrong sitting. */
      const scans = sittings
        .filter((s) => String(s.exam_year ?? "").trim()
          && s.subjects.some((x) => x.subject.trim() && x.grade.trim()))
        .map((s, i) => ({ file: s.file, i }))
        .filter((x) => x.file);

      let body: any = document;

      if (picked.length || scans.length) {
        const fd = new FormData();
        // The whole document as one field: flattening a deeply nested form into
        // FormData keys would be a second serialiser to keep in step with the
        // validator. The server decodes this back before validating.
        fd.append("payload", JSON.stringify(document));
        picked.forEach(({ file, i }) => fd.append(`qualification_files[${i}]`, file));
        // One scan per sitting — it is one result slip, not one per subject.
        scans.forEach(({ file, i }) => fd.append(`sitting_files[${i}]`, file as File));
        body = fd;
      }

      const res: any = await save({ id: applicationId, body }).unwrap();

      setMissing(res?.missing ?? []);
      setBanner({
        ok: !!res?.complete,
        text: res?.complete
          ? "Application form saved and complete."
          : "Saved. Some sections are still outstanding.",
      });
      onSaved?.(!!res?.complete);
    } catch (e: any) {
      const errors = e?.data?.errors ? Object.values(e.data.errors).flat().join(" ") : null;
      setBanner({ ok: false, text: errors || e?.data?.message || "Could not save the form." });
    }
  };

  const summary = useMemo(
    () => [
      { label: "O'level", done: olevelCount >= requiredOlevels, detail: `${olevelCount}/${requiredOlevels}` },
      { label: "Qualifications", done: educations.length > 0, detail: String(educations.length) },
      { label: "Experience", done: experiences.length > 0, detail: String(experiences.length) },
      { label: "Employer", done: !!employer?.name, detail: employer?.name ? "✓" : "—" },
      { label: "Sponsor", done: !!sponsor?.full_name, detail: sponsor?.full_name ? "✓" : "—" },
      { label: "Declaration", done: !!form?.declaration_accepted, detail: form?.declaration_accepted ? "✓" : "—" },
    ],
    [olevelCount, educations.length, experiences.length, employer, sponsor, form, requiredOlevels]
  );

  if (isLoading) {
    return (
      <div className="bg-white border rounded-xl p-6 flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your application form…
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="bg-white border rounded-xl p-6 text-sm text-rose-700">
        {data?.message || "Could not load the application form."}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Heading + progress */}
      <div className="bg-white border rounded-xl p-5">
        <h2 className="font-semibold text-gray-900">Application form</h2>
        <p className="text-sm text-gray-500 mt-1">
          This exam requires the full NIQS application form. It is submitted together with your
          application and endorsed by your chapter.
        </p>

        {prefilled && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-sky-50 border border-sky-100 text-xs text-sky-800">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            We've filled in what the Institute already holds from your records. Check every section,
            correct anything out of date, then save. Your O'level results and academic
            qualifications are kept on your Institute record, so corrections here carry over to
            your next application — applications you have already submitted are not changed.
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {summary.map((s) => (
            <span
              key={s.label}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                s.done
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-gray-50 text-gray-500 border-gray-200"
              }`}
            >
              {s.done ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
              {s.label} <span className="opacity-70">{s.detail}</span>
            </span>
          ))}
        </div>

        {banner && (
          <div
            className={`mt-4 px-3 py-2 rounded-lg text-xs border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                : "bg-amber-50 border-amber-100 text-amber-800"
            }`}
          >
            {banner.text}
            {missing.length > 0 && (
              <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <fieldset disabled={readOnly} className="space-y-4">
        {/* 1.0 Personal details */}
        <Section n="1.0" title="Personal details" hint="As held by the Institute — update these in your profile">
          {/* These are required to submit but cannot be typed here, so a blank
              one is a dead end unless we say where to go and fix it. */}
          {heldGaps.length > 0 && (
            <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Your record is missing {heldGaps.join(", ")}. These are required, and cannot be
                entered here — update your profile, then reload this page.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Held label="NIQS membership number" value={form.membership_no} />
            <Held label="Title" value={form.title} />
            <Held label="Surname" value={form.surname} />
            <Held label="Other names" value={form.other_names} />
            <Held label="Gender" value={GENDERS[form.gender] ?? form.gender} />
            <Held label="Date of birth" value={form.date_of_birth} />

            {/* The one detail the Institute may not hold — ask for it once. */}
            {needsChapter ? (
              <div>
                <label className={label}>Chapter *</label>
                <select
                  className={input}
                  value={form.chapter_id ?? ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    setF("chapter_id", id);
                    setF("chapter", chapters.find((c) => c.id === id)?.name ?? "");
                  }}
                >
                  <option value="">Select your chapter…</option>
                  {chapters.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-gray-400">
                  We have no chapter on your record. This is saved to your membership too.
                </p>
              </div>
            ) : (
              <Held label="Chapter" value={form.chapter} />
            )}

            <Held label="Phone number" value={form.phone} />
            <Held label="Email address" value={form.email} />
            <Held label="Current membership grade" value={form.membership_grade} />
            <Held label="Date of registration" value={form.date_of_registration} />
            <Held label="Diet" value={form.diet} />
            <Held label="Postal address" value={form.postal_address} wide />

            {/* Not on file anywhere — theirs to declare, and always in view so it
                is never missed by someone who does need arrangements made. */}
            <div className="sm:col-span-2 space-y-2 pt-1 border-t">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!form.disability}
                  onChange={(e) => setF("disability", e.target.checked)}
                />
                I have a disability the Institute should be aware of
              </label>
              <input
                className={input}
                placeholder="Please describe, so we can make arrangements"
                value={form.disability_details ?? ""}
                onChange={(e) => setF("disability_details", e.target.value)}
              />
            </div>
          </div>
        </Section>

        {/* 2.0 Preferred interview centre */}
        <Section n="2.0" title="Preferred interview centre" hint="Centres running this exam in this diet" defaultOpen={false}>
          {centres.length === 0 ? (
            <p className="text-sm text-gray-500">
              No centres have been enabled for this exam in this diet yet.
            </p>
          ) : (
            <select
              className={input}
              value={form.preferred_centre ?? ""}
              onChange={(e) => setF("preferred_centre", e.target.value)}
            >
              <option value="">Select a centre…</option>
              {centres.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}{c.state ? ` — ${c.state}` : ""}
                </option>
              ))}
            </select>
          )}
        </Section>

        {/* 3.0 O'level — asked for by SITTING, the way the probationer
            application asks and the way the candidate was examined: one board,
            one year, one exam number and one scanned slip, with the subjects
            taken in it inside. The server expands them into the subject rows
            the table holds.

            THIS is the O'level editor whenever the form is on screen. The
            hand-in section below leaves O'level alone in that case — two
            editors over one table is how a sitting loses its exam number and
            its upload. */}
        <Section
          n="3.0"
          title="O'level results"
          hint={`${requiredOlevels} credits from WAEC / NECO / NABTEB`}
        >
          {olevelShort && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
              {olevelCount} of {requiredOlevels} subjects entered.
            </div>
          )}

          <OlevelSittings
            sittings={sittings}
            onChange={setSittings}
            disabled={readOnly}
            onError={(text) => setBanner({ ok: false, text })}
          />
        </Section>

        {/* 4.0 Education */}
        {/* 4.0 — the academic qualifications themselves, certificate and all.
            The same records the member's Institute record holds, so this behaves
            like the O'level section above: pre-filled from what we already have,
            and saved back to the record for the next application. */}
        <Section
          n="4.0"
          title="Academic qualifications"
          hint="With the certificate for each — kept on your Institute record"
        >
          <Rows
            items={educations}
            onAdd={() => setEducations((p) => [...p, { institution: "", qualification: "" }])}
            onRemove={(i) => setEducations((p) => p.filter((_, idx) => idx !== i))}
            addLabel="Add qualification"
            empty="No qualifications entered yet."
          >
            {(row, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="sm:col-span-2">
                  <label className={label}>University / institution</label>
                  <input className={input} value={row.institution ?? ""} onChange={(e) => setRow(setEducations)(i, "institution", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Country</label>
                  <input className={input} value={row.country ?? ""} onChange={(e) => setRow(setEducations)(i, "country", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Year graduated</label>
                  {/* A year, stored as a date. The probationer form and the
                      member record only ever asked for the year, so asking for
                      a day and a month would be inventing precision. */}
                  <input
                    className={input}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="e.g. 2018"
                    value={(row.completed_on ?? "").slice(0, 4)}
                    onChange={(e) => {
                      const year = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setRow(setEducations)(i, "completed_on", year.length === 4 ? `${year}-01-01` : year);
                    }}
                  />
                </div>
                <div className="sm:col-span-4">
                  <label className={label}>Degree / diploma</label>
                  <input className={input} value={row.qualification ?? ""} onChange={(e) => setRow(setEducations)(i, "qualification", e.target.value)} placeholder="e.g. BSc in Quantity Surveying" />
                </div>

                <div className="sm:col-span-4">
                  <label className={label}>Certificate</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {row.certificate_file && !row.file ? (
                      <a
                        href={storageUrl(row.certificate_file) ?? "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-medium hover:bg-emerald-100"
                      >
                        <FileCheck className="w-3.5 h-3.5" /> On file
                      </a>
                    ) : null}

                    {row.file ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 text-sky-700 border border-sky-100 text-xs font-medium">
                        <FileText className="w-3.5 h-3.5" />
                        <span className="max-w-[10rem] truncate">{row.file.name}</span>
                        <button
                          type="button"
                          onClick={() => setRow(setEducations)(i, "file", null)}
                          className="p-0.5 hover:bg-sky-100 rounded"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : null}

                    <label className="relative cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => {
                          setRow(setEducations)(i, "file", e.target.files?.[0] ?? null);
                          e.target.value = "";
                        }}
                      />
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs font-bold text-gray-500 hover:border-gray-400">
                        <UploadCloud className="w-3.5 h-3.5" />
                        {row.file || row.certificate_file ? "Replace" : "Upload certificate"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </Rows>
        </Section>

        {/* 5.0 Other professional institutions */}
        <Section n="5.0" title="Membership of other professional institutions" hint="If any" defaultOpen={false}>
          <Rows
            items={memberships}
            onAdd={() => setMemberships((p) => [...p, { institution: "" }])}
            onRemove={(i) => setMemberships((p) => p.filter((_, idx) => idx !== i))}
            addLabel="Add institution"
            empty="None — leave empty if this does not apply to you."
          >
            {(row, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div>
                  <label className={label}>Institution</label>
                  <input className={input} value={row.institution ?? ""} onChange={(e) => setRow(setMemberships)(i, "institution", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Current grade</label>
                  <input className={input} value={row.current_grade ?? ""} onChange={(e) => setRow(setMemberships)(i, "current_grade", e.target.value)} />
                </div>
                <div>
                  <label className={label}>How achieved</label>
                  <input className={input} value={row.how_achieved ?? ""} onChange={(e) => setRow(setMemberships)(i, "how_achieved", e.target.value)} placeholder="e.g. Examination" />
                </div>
                <div>
                  <label className={label}>Year obtained</label>
                  <input className={input} value={row.year_obtained ?? ""} onChange={(e) => setRow(setMemberships)(i, "year_obtained", e.target.value)} />
                </div>
              </div>
            )}
          </Rows>
        </Section>

        {/* 6.0 TPC / logbook particulars */}
        <Section n="6.0" title="Examinations and logbook stages passed" hint="Your NIQS record, plus anything passed elsewhere">
          {/* What the Institute already holds — read back, never re-typed. */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
              From your NIQS record
            </p>

            {systemLogbooks.length === 0 && systemExams.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">
                We have no released logbook or exam results on your record yet.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {systemLogbooks.map((lb) => (
                  <li key={`lb-${lb.stage}`} className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="font-medium">{lb.label}</span>
                    {/* The stage and when it was passed, not the mark behind it.
                        This section exists to confirm what the candidate has
                        cleared; the assessors' score is not part of that and is
                        no longer sent by the server either. */}
                    {lb.final_grade && <span className="text-gray-500">{lb.final_grade}</span>}
                    <span className="text-gray-400">{fmtDay(lb.passed_on)}</span>
                  </li>
                ))}
                {systemExams.map((x, i) => (
                  <li key={`ex-${i}`} className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="font-medium">{x.exam_title ?? x.exam_code ?? "Exam"}</span>
                    {x.diet && <span className="text-gray-500">{x.diet}</span>}
                    {x.exam_no && <span className="text-gray-400">{x.exam_no}</span>}
                    <span className="text-gray-400">{fmtDay(x.passed_on)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Only what NIQS has no way of knowing is typed. */}
          <p className="mt-5 mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
            Exams passed elsewhere
          </p>
          <Rows
            items={otherExams}
            onAdd={() => setOtherExams((p) => [...p, { exam_name: "" }])}
            onRemove={(i) => setOtherExams((p) => p.filter((_, idx) => idx !== i))}
            addLabel="Add exam"
            empty="None — add any exam you passed outside NIQS, such as RICS."
          >
            {(row, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="sm:col-span-2">
                  <label className={label}>Examination</label>
                  <input className={input} value={row.exam_name ?? ""} onChange={(e) => setRow(setOtherExams)(i, "exam_name", e.target.value)} placeholder="e.g. RICS APC" />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Awarding body</label>
                  <input className={input} value={row.awarding_body ?? ""} onChange={(e) => setRow(setOtherExams)(i, "awarding_body", e.target.value)} placeholder="e.g. RICS" />
                </div>
                <div>
                  <label className={label}>Year</label>
                  <input className={input} value={row.exam_year ?? ""} onChange={(e) => setRow(setOtherExams)(i, "exam_year", e.target.value)} placeholder="2018" />
                </div>
                <div>
                  <label className={label}>Exam / membership no.</label>
                  <input className={input} value={row.exam_number ?? ""} onChange={(e) => setRow(setOtherExams)(i, "exam_number", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Result</label>
                  <input className={input} value={row.result ?? ""} onChange={(e) => setRow(setOtherExams)(i, "result", e.target.value)} placeholder="e.g. Passed" />
                </div>
              </div>
            )}
          </Rows>
        </Section>

        {/* 7.0 Professional experience */}
        <Section n="7.0" title="Professional experience">
          <Rows
            items={experiences}
            onAdd={() => setExperiences((p) => [...p, { employer_name: "", position: "" }])}
            onRemove={(i) => setExperiences((p) => p.filter((_, idx) => idx !== i))}
            addLabel="Add post"
            empty="No experience entered yet."
          >
            {(row, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="sm:col-span-2">
                  <label className={label}>Employer name</label>
                  <input className={input} value={row.employer_name ?? ""} onChange={(e) => setRow(setExperiences)(i, "employer_name", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Position held</label>
                  <input className={input} value={row.position ?? ""} onChange={(e) => setRow(setExperiences)(i, "position", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={label}>From</label>
                    <input type="date" className={input} value={row.start_date ?? ""} onChange={(e) => setRow(setExperiences)(i, "start_date", e.target.value)} />
                  </div>
                  <div>
                    <label className={label}>To</label>
                    <input type="date" className={input} value={row.end_date ?? ""} onChange={(e) => setRow(setExperiences)(i, "end_date", e.target.value)} />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Employer address</label>
                  <input className={input} value={row.employer_address ?? ""} onChange={(e) => setRow(setExperiences)(i, "employer_address", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Duties</label>
                  <input className={input} value={row.duties ?? ""} onChange={(e) => setRow(setExperiences)(i, "duties", e.target.value)} />
                </div>
                <p className="sm:col-span-4 text-[11px] text-gray-400">
                  Leave "To" empty if you are still in this post.
                </p>
              </div>
            )}
          </Rows>
        </Section>

        {/* 8.0 Employer */}
        <Section n="8.0" title="Employer details" hint="Your present employer attests to these particulars">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={label}>Name of employer</label>
              <input className={input} value={employer.name ?? ""} onChange={(e) => setE("name", e.target.value)} />
            </div>
            <div>
              <label className={label}>Nature of business</label>
              <input className={input} value={employer.nature_of_business ?? ""} onChange={(e) => setE("nature_of_business", e.target.value)} />
            </div>
            <div>
              <label className={label}>Your position</label>
              <input className={input} value={employer.applicant_position ?? ""} onChange={(e) => setE("applicant_position", e.target.value)} />
            </div>
            <div>
              <label className={label}>Date of employment</label>
              <input type="date" className={input} value={employer.employment_date ?? ""} onChange={(e) => setE("employment_date", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Business address</label>
              <textarea rows={2} className={input} value={employer.business_address ?? ""} onChange={(e) => setE("business_address", e.target.value)} />
            </div>
          </div>
          <p className="mt-3 text-[11px] text-gray-400">
            Your employer signs this section on the printed form.
          </p>
        </Section>

        {/* 9.0 Sponsor */}
        <Section n="9.0" title="Sponsor details" hint="A NIQS member who sponsors your application">
          {/* Name the sponsor by their membership number and we fill the rest,
              so a typo in their name can't misidentify who is sponsoring. */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 mb-4">
            <label className={label}>Find your sponsor by membership number</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                className={`${input} flex-1`}
                value={sponsor.membership_no ?? ""}
                disabled={readOnly}
                onChange={(e) => {
                  // The particulars below belong to the number that fetched
                  // them. Changing it drops them rather than leaving one
                  // member's name standing under another's membership number.
                  setSponsor({ membership_no: e.target.value });
                  setSponsorHit(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onFindSponsor();
                  }
                }}
                placeholder="e.g. F00123A"
              />
              <button
                type="button"
                onClick={onFindSponsor}
                disabled={readOnly || looking || !String(sponsor.membership_no ?? "").trim()}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50 shrink-0"
              >
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {looking ? "Searching…" : "Search"}
              </button>
            </div>

            {sponsorHit && (
              <p
                className={`mt-2 flex items-start gap-1.5 text-xs ${
                  sponsorHit.ok ? "text-emerald-700" : "text-rose-700"
                }`}
              >
                {sponsorHit.ok
                  ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  : <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                {sponsorHit.text}
              </p>
            )}

            <p className="mt-2 text-[11px] text-gray-400">
              Your sponsor must be a NIQS member. Search by their membership number — their
              particulars are filled in from the Institute's own record.
            </p>
          </div>

          {/* READ-ONLY, and filled only by the search above.
              A sponsor is a specific member, and the whole point of naming them
              by membership number is that the form says who the Institute has
              on record rather than what the candidate typed. Left editable, a
              corrected spelling or an out-of-date grade would quietly turn the
              sponsorship into a claim about somebody who may not exist.

              Enough to know the right member was found, and no more: their
              REGISTRATION DATE is not the candidate's business, and
              sponsorLookup() no longer sends one. */}
          {sponsor.full_name ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Held label="Sponsor's full name" value={sponsor.full_name} />
              <Held label="Membership number" value={sponsor.membership_no} />
              <Held label="Membership grade" value={sponsor.membership_grade} />
              <Held label="Sponsor's email" value={sponsor.email} />
              <Held label="Contact address" value={sponsor.contact_address} wide />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
              No sponsor chosen yet. Search for their membership number above.
            </div>
          )}

          <p className="mt-3 text-[11px] text-gray-400">
            Your sponsor signs this section on the printed form.
          </p>
        </Section>

        {/* 10.0 Declaration */}
        <Section n="10.0" title="Applicant's declaration">
          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 mt-0.5"
              checked={!!form.declaration_accepted}
              onChange={(e) => setF("declaration_accepted", e.target.checked)}
            />
            <span>
              I declare that the particulars given on this form are true and correct, and that I will
              abide by the Rules and Regulations governing the Institute's Examinations. By submitting
              this form I agree to be bound by all existing NIQS Examination Policies, Rules and
              Guidelines, and the rules guiding this professional examination in particular.
            </span>
          </label>
        </Section>
      </fieldset>

      {!readOnly && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">
            Section 11 — your chapter chairman or secretary endorses the form separately.
          </p>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save form
          </button>
        </div>
      )}
    </div>
  );
}
